import { getApiBaseUrl, getApiKey } from '~/modules/Auth/authService';

export const KARAOKE_BOT_SENDER_ID = 'karaokebot';

export interface KaraokeBotMessage {
  id: string;
  senderId: string;
  fromMe: boolean;
  text: string;
  sentAt: string;
  time: string;
}

export interface KaraokeBotChat {
  id: string;
  messages: KaraokeBotMessage[];
}

interface RequestOptions extends RequestInit {
  token: string;
}

interface ApiPayload {
  success?: boolean;
  message?: string;
  response_message?: string;
  chat?: unknown;
  chat_id?: string;
  stored_message?: unknown;
}

const REQUEST_TIMEOUT_MS = 25000;
const POLL_INTERVAL_MS = 2000;
const RESPONSE_TIMEOUT_MS = 60000;
const CHAT_UPDATED_EVENT = 'karaokebot:chat-updated';
const chatCache = new Map<string, KaraokeBotChat>();
const chatRequests = new Map<string, Promise<KaraokeBotChat>>();

function cacheChat(currentUserId: string, chat: KaraokeBotChat): KaraokeBotChat {
  chatCache.set(currentUserId, chat);
  window.dispatchEvent(new CustomEvent(CHAT_UPDATED_EVENT, { detail: { userId: currentUserId, chatId: chat.id } }));
  return chat;
}

function getApiUrl(path: string): string {
  const base = getApiBaseUrl().replace(/\/+$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

async function apiRequest(path: string, { token, headers, body, ...options }: RequestOptions): Promise<ApiPayload> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const requestHeaders = new Headers(headers);
  const apiKey = getApiKey();

  requestHeaders.set('Accept', 'application/json');
  requestHeaders.set('Authorization', `Bearer ${token}`);
  requestHeaders.set('auth', token);

  if (apiKey) {
    requestHeaders.set('X-API-Key', apiKey);
  }

  if (body && !requestHeaders.has('Content-Type')) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  try {
    const response = await fetch(getApiUrl(path), {
      ...options,
      body,
      credentials: 'include',
      mode: 'cors',
      headers: requestHeaders,
      signal: controller.signal,
    });

    const payload = (await response.json().catch(() => null)) as ApiPayload | null;

    if (!response.ok || !payload || payload.success === false) {
      throw new Error(payload?.message || payload?.response_message || 'KaraokeBot request failed.');
    }

    return payload;
  } finally {
    window.clearTimeout(timeout);
  }
}

function parseBackendDate(raw: unknown): Date | null {
  if (!raw) return null;

  let value = String(raw).replace(' ', 'T');
  if (!value.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(value)) {
    value += 'Z';
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeMessage(message: unknown, currentUserId: string): KaraokeBotMessage | null {
  if (!message || typeof message !== 'object') return null;

  const source = message as Record<string, unknown>;
  const text = String(source.message ?? '').trim();
  if (!text) return null;

  const senderId = String(source.idUserSender ?? '');
  const parsedDate = parseBackendDate(source.sentAt);

  return {
    id: String(source._id ?? `${senderId}:${String(source.sentAt ?? '')}:${text}`),
    senderId,
    fromMe: senderId === currentUserId,
    text,
    sentAt: parsedDate?.toISOString() ?? new Date().toISOString(),
    time: (parsedDate ?? new Date()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  };
}

function normalizeChat(payload: ApiPayload, currentUserId: string): KaraokeBotChat {
  const chat = payload.chat;
  if (!chat || typeof chat !== 'object') {
    throw new Error('KaraokeBot chat was not returned by the API.');
  }

  const source = chat as Record<string, unknown>;
  const messages = Array.isArray(source.messages) ? source.messages : [];

  return {
    id: String(source._id ?? ''),
    messages: messages
      .map((message) => normalizeMessage(message, currentUserId))
      .filter((message): message is KaraokeBotMessage => Boolean(message)),
  };
}

export function resolveKaraokeBotUserId(user: unknown): string | null {
  if (!user || typeof user !== 'object') return null;
  const candidate = user as Record<string, unknown>;
  const id = candidate._id ?? candidate.id ?? candidate.id_user ?? candidate.user_id;
  return id == null ? null : String(id);
}

export function getCachedKaraokeBotChat(currentUserId: string): KaraokeBotChat | null {
  return chatCache.get(currentUserId) ?? null;
}

export function onKaraokeBotChatUpdated(currentUserId: string, listener: (chat: KaraokeBotChat) => void): () => void {
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ userId?: string }>).detail;
    if (detail?.userId !== currentUserId) return;

    const cached = chatCache.get(currentUserId);
    if (cached) listener(cached);
  };

  window.addEventListener(CHAT_UPDATED_EVENT, handler);
  return () => window.removeEventListener(CHAT_UPDATED_EVENT, handler);
}

export async function fetchKaraokeBotChat(
  token: string,
  currentUserId: string,
  options: { force?: boolean } = {},
): Promise<KaraokeBotChat> {
  if (!options.force) {
    const cached = chatCache.get(currentUserId);
    if (cached) return cached;
  }

  const pending = chatRequests.get(currentUserId);
  if (pending) return pending;

  const request = apiRequest('/karaoke-bot/chat', { method: 'GET', token })
    .then((payload) => cacheChat(currentUserId, normalizeChat(payload, currentUserId)))
    .finally(() => chatRequests.delete(currentUserId));

  chatRequests.set(currentUserId, request);
  return request;
}

/**
 * Sends the user's message and returns the stored message id, used to detect the bot reply.
 */
export async function sendKaraokeBotMessage(
  token: string,
  currentUserId: string,
  chatId: string,
  message: string,
): Promise<string> {
  const payload = await apiRequest('/karaoke-bot/message', {
    method: 'POST',
    token,
    body: JSON.stringify({ id_chat: chatId, message }),
  });

  const stored = normalizeMessage(payload.stored_message, currentUserId);
  if (!stored) {
    throw new Error('KaraokeBot did not store the message.');
  }

  const cached = chatCache.get(currentUserId);
  if (cached?.id === chatId) {
    cacheChat(currentUserId, { ...cached, messages: [...cached.messages, stored] });
  }

  return stored.id;
}

/**
 * Polls the chat until the bot replies to the message identified by `userMessageId`.
 */
export async function waitForKaraokeBotReply(
  token: string,
  currentUserId: string,
  userMessageId: string,
): Promise<KaraokeBotChat> {
  const startedAt = Date.now();

  while (Date.now() - startedAt < RESPONSE_TIMEOUT_MS) {
    await new Promise((resolve) => window.setTimeout(resolve, POLL_INTERVAL_MS));

    const chat = await fetchKaraokeBotChat(token, currentUserId, { force: true });
    const userMessageIndex = chat.messages.findIndex((message) => message.id === userMessageId);
    const replied =
      userMessageIndex >= 0 &&
      chat.messages.slice(userMessageIndex + 1).some((message) => message.senderId === KARAOKE_BOT_SENDER_ID);

    // A missing user message means the chat was cleared meanwhile: stop waiting.
    if (replied || userMessageIndex < 0) return chat;
  }

  throw new Error('KaraokeBot is still preparing the answer. Try again in a few seconds.');
}

export async function clearKaraokeBotChat(token: string, currentUserId: string): Promise<KaraokeBotChat> {
  const payload = await apiRequest('/karaoke-bot/chat', { method: 'DELETE', token });
  return cacheChat(currentUserId, normalizeChat(payload, currentUserId));
}
